/**
 * Project status — Active / Inactive.
 *
 * A grouping label on `building_projects.project_status`, distinct from
 * archiving ("Delete project"): an inactive project stays fully usable. Every
 * project choice in the app groups or filters on it; these helpers are the one
 * implementation of that.
 */

import { STATUS_PALETTE, type StatusPaletteEntry } from "@/domains/pm/statuses/statusPalette";
import type { ProjectStatus } from "@/types/projects";

import { ensureProjectStatusAvailable, isProjectStatusEnabled } from "./projectStatusAvailability";

export type { ProjectStatus };

/** What a status filter can show. */
export type ProjectStatusFilterValue = ProjectStatus | "all";

/** Anything carrying an id and a status — picker options, full project rows. */
export interface ProjectWithStatus {
  id: number | string;
  status: ProjectStatus;
}

/**
 * A project choice splits by status (the picker's tabs, the filter beside the
 * other choices) only past this many projects. At or below it everything is
 * one list, and each row's status dot is enough to tell them apart.
 */
export const PROJECT_STATUS_SPLIT_THRESHOLD = 8;

/** Components pass `useProjectStatusEnabled()` so they re-render when the
 *  availability probe settles. */
export function shouldSplitByStatus(
  projectCount: number,
  enabled: boolean = isProjectStatusEnabled()
): boolean {
  return enabled && projectCount > PROJECT_STATUS_SPLIT_THRESHOLD;
}

/**
 * A select list with `project_status` appended — only once the database is
 * known to have the column (see projectStatusAvailability). Naming it before
 * the migration applies would fail the whole query with 42703.
 */
export async function projectStatusProjection(columns: string): Promise<string> {
  return (await ensureProjectStatusAvailable()) ? `${columns}, project_status` : columns;
}

/**
 * Narrow a raw `project_status` column value. Anything unrecognised — a row
 * read before the migration applied, where the column is absent — reads as
 * active, which is what every project was before statuses existed.
 */
export function toProjectStatus(value: unknown): ProjectStatus {
  return value === "inactive" ? "inactive" : "active";
}

export function partitionProjectsByStatus<T extends ProjectWithStatus>(
  projects: readonly T[]
): { active: T[]; inactive: T[] } {
  const active: T[] = [];
  const inactive: T[] = [];
  for (const project of projects) {
    (project.status === "inactive" ? inactive : active).push(project);
  }
  return { active, inactive };
}

/**
 * Active projects first, then inactive — each group keeping the order it came
 * in (name, recency, …). How every single, unsplit project list is ordered.
 */
export function orderActiveFirst<T extends ProjectWithStatus>(projects: readonly T[]): T[] {
  const { active, inactive } = partitionProjectsByStatus(projects);
  return [...active, ...inactive];
}

const sameId = (a: number | string, b: number | string) => String(a) === String(b);

/**
 * The projects a filter shows. `keepId` — the currently selected project — is
 * kept even when its status doesn't match: a Radix `Select` paints nothing for
 * a value no mounted item carries, so filtering the selection out would blank
 * the field the user is looking at.
 */
export function filterProjectsByStatus<T extends ProjectWithStatus>(
  projects: readonly T[],
  filter: ProjectStatusFilterValue,
  keepId?: number | string | null
): T[] {
  if (filter === "all") return [...projects];
  return projects.filter(
    (project) => project.status === filter || (keepId != null && sameId(project.id, keepId))
  );
}

/** The tab or filter a picker opens on: the one holding the selection, else Active. */
export function initialStatusTab(
  projects: readonly ProjectWithStatus[],
  selectedId?: number | string | null
): ProjectStatus {
  if (selectedId == null) return "active";
  const selected = projects.find((project) => sameId(project.id, selectedId));
  return selected?.status ?? "active";
}

const paletteEntry = (id: string): StatusPaletteEntry =>
  STATUS_PALETTE.find((entry) => entry.id === id) ?? STATUS_PALETTE[0];

/** Same palette as the PM status chips, so a project's status reads like an issue's. */
export const PROJECT_STATUS_COLOURS: Record<ProjectStatus, StatusPaletteEntry> = {
  active: paletteEntry("green"),
  inactive: paletteEntry("grey"),
};

/** The filter's options, in display order. */
export function statusFilterOptionValues(includeAll: boolean): ProjectStatusFilterValue[] {
  return includeAll ? ["active", "inactive", "all"] : ["active", "inactive"];
}
